// Kalendář: den / týden / měsíc / agenda, více kalendářů, tažení (vytvořit, přesunout, prodloužit), připomenutí, import .ics.
import { simpleBar } from '@core/components/chrome';
import { registerComponent } from '@core/views/factory';
import { action, runCommand, ApiError } from '@core/api';
import { h, clear, clamp, debounce } from '@core/dom';
import { t, tk, currentLang, fmtTime } from '@core/i18n';
import { icon } from '@core/icons';
import { IwRibbon, type RibbonButton } from '@core/components/ribbon';
import { IwSplit, emptyState } from '@core/components/misc';
import { IwCommandBar, type BarItem } from '@core/components/commandbar';
import { useBar, renderBarHeader, fabButton, HeadSearch } from '@core/components/chrome';
import { contextMenu, type MenuItem } from '@core/components/menu';
import { prompt, confirm, dialog } from '@core/components/dialog';
import { toast } from '@core/components/toast';
import { events } from '@core/events';
import { savePref } from '@core/theme';
import { pickFile } from '@core/shell/picker';
import type { StorageItem } from '@core/contract';

interface Cal { id: string; name: string; kind: string; color: string; level: string; eventsWritable: boolean }
interface Payload { calendars: Cal[]; defaultCalendar: string | null; tz: string; birthdays: boolean; date: string | null; view: string | null }
interface Occ { id: string; uid: string; calendarId: string; summary: string; location: string | null; allDay: boolean; status: string; color: string; editable: boolean; recurring: boolean; exception: boolean; start: string; end: string; instanceStart: string | null; contactId?: string; s: Date; e: Date }
type View = 'day' | 'week' | 'workweek' | 'month' | 'agenda';
/** Nastavení zobrazení (předvolby uživatele `kalendar.*`): první den týdne, pracovní dny (0 = ne … 6 = so), pracovní doba v celých hodinách. */
interface Settings { firstDay: number; workDays: number[]; workStart: number; workEnd: number }

const HOUR_PX = 48;
const SNAP = 30;
const DAY_MS = 86400000;
const LANE_PX = 21;

const startOfDay = (d: Date): Date => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number): Date => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d: Date, firstDay = 1): Date => addDays(startOfDay(d), -((d.getDay() - firstDay + 7) % 7));
const sameDay = (a: Date, b: Date): boolean => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const ymd = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmt = (d: Date, o: Intl.DateTimeFormatOptions): string => new Intl.DateTimeFormat(currentLang(), o).format(d);
const ucfirst = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const minutesOf = (d: Date): number => d.getHours() * 60 + d.getMinutes();
const prefs = (): Record<string, unknown> => (window as unknown as { iwanPrefs?: Record<string, unknown> }).iwanPrefs ?? {};
const numPref = (v: unknown, dflt: number): number => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : dflt);
const settings = (): Settings => {
  const pr = prefs();
  const fd = numPref(pr['kalendar.firstDay'], 1);
  const wd = Array.isArray(pr['kalendar.workDays']) ? (pr['kalendar.workDays'] as unknown[]).filter((x): x is number => typeof x === 'number' && x >= 0 && x <= 6) : [1, 2, 3, 4, 5];
  const ws = clamp(numPref(pr['kalendar.workStart'], 8), 0, 23);
  return { firstDay: [0, 1, 6].includes(fd) ? fd : 1, workDays: wd, workStart: ws, workEnd: clamp(numPref(pr['kalendar.workEnd'], 17), ws + 1, 24) };
};
/** Den v týdnu s daným číslem (0 = ne … 6 = so) – pro názvy dnů přes Intl. 18. 10. 2026 je neděle. */
const weekdayDate = (dow: number): Date => addDays(new Date(2026, 9, 18), dow);

registerComponent('kalendar-view', ({ desc, win, wm }) => {
  const p = desc.payload as unknown as Payload;
  let calendars = p.calendars;
  const hidden = new Set<string>(Array.isArray(prefs()['kalendar.hidden']) ? (prefs()['kalendar.hidden'] as string[]) : []);
  let birthdays = p.birthdays && prefs()['kalendar.birthdays'] !== false;
  let view: View = (p.view as View) || ((prefs()['kalendar.view'] as View) ?? 'week');
  let cursor = p.date ? startOfDay(new Date(p.date)) : startOfDay(new Date());
  let cfg = settings();
  const sow = (d: Date): Date => startOfWeek(d, cfg.firstDay);
  let occs: Occ[] = [];
  let rangeFrom = new Date();
  let rangeTo = new Date();
  const shownReminders = new Set<string>();
  // hledání v záhlaví (vzor Kontakty): výsledky jako seznam termínů místo mřížky, ← / vymazání vrátí pohled
  let searchQ = '';
  const hs = useBar() ? new HeadSearch((q) => { searchQ = q; void load(); }) : null;
  const isMobile = (): boolean => wm.currentMode() === 'mobile';

  const ribbon = new IwRibbon();
  // koncepce A (postup 69): záhlaví s lištou, ☰ panel (měsíc + kalendáře), telefon ⋮ a plovoucí Nový termín
  const bar = useBar() ? new IwCommandBar() : null;
  const fab = fabButton();
  const split = new IwSplit();
  const side = h('div', { class: 'cal-side' });
  const head = h('div', { class: 'cal-head' });
  const body = h('div', { class: 'cal-body' });
  split.first.appendChild(side);
  split.second.append(head, body);
  split.setWidth(230);
  const el = bar ? h('div', { class: 'view cal-view chrome-bar' }, split, fab) : h('div', { class: 'view cal-view' }, ribbon, split);
  if (bar) bar.globalLabel = t('kalendar.name');
  let narrow = false;
  let sideHidden = false;
  let backSide = 0;
  const ro = new ResizeObserver(() => {
    const n = el.clientWidth > 0 && el.clientWidth < 700;
    if (!el.clientWidth || n === narrow) return;
    narrow = n;
    split.setOverlay(n);
    setSide(n);
    if (n && (view === 'week' || view === 'workweek')) setView('day');
  });
  ro.observe(el);
  function setSide(hide: boolean): void {
    sideHidden = hide;
    split.setFirstHidden(hide);
    // vysunutý panel (úzké okno, telefon): zpět ho zavře, klepnutí vedle taky
    if (!hide && narrow && !wm.hasBack(backSide)) backSide = wm.pushBack(() => { backSide = 0; setSide(true); return true; }, win);
    if (hide && backSide) {
      const id = backSide;
      backSide = 0;
      wm.dropBack(id);
    }
    refreshRibbon();
  }
  split.second.addEventListener('pointerdown', () => { if (narrow && !sideHidden) setSide(true); }, true);

  // ---- ribbon
  const calOf = (id: string): Cal | undefined => calendars.find((c) => c.id === id);
  const writableCal = (): string | null => (p.defaultCalendar && calOf(p.defaultCalendar)?.eventsWritable ? p.defaultCalendar : calendars.find((c) => c.eventsWritable)?.id ?? null);
  const buttons = (): RibbonButton[] => [
    { id: 'new', label: t('kalendar.cmd.new'), icon: 'plus', shortcut: 'Ctrl+N', group: 'core.grp.new', primary: true, disabled: !writableCal(), onClick: () => openNew() },
    { id: 'today', label: t('kalendar.cmd.today'), icon: 'calendar', group: 'kalendar.grp.nav', order: -20, onClick: () => go(startOfDay(new Date())) },
    { id: 'prev', label: t('kalendar.cmd.prev'), icon: 'chevron-left', group: 'kalendar.grp.nav', order: -20, onClick: () => step(-1) },
    { id: 'next', label: t('kalendar.cmd.next'), icon: 'chevron', group: 'kalendar.grp.nav', order: -20, onClick: () => step(1) },
    { id: 'v_day', label: t('kalendar.cmd.day'), icon: 'window', group: 'kalendar.grp.view', order: -10, toggle: true, active: view === 'day', onClick: () => setView('day') },
    { id: 'v_week', label: t('kalendar.cmd.week'), icon: 'columns', group: 'kalendar.grp.view', order: -10, toggle: true, active: view === 'week', onClick: () => setView('week') },
    { id: 'v_workweek', label: t('kalendar.cmd.workweek'), icon: 'briefcase', group: 'kalendar.grp.view', order: -10, toggle: true, active: view === 'workweek', onClick: () => setView('workweek') },
    { id: 'v_month', label: t('kalendar.cmd.month'), icon: 'grid', group: 'kalendar.grp.view', order: -10, toggle: true, active: view === 'month', onClick: () => setView('month') },
    { id: 'v_agenda', label: t('kalendar.cmd.agenda'), icon: 'list', group: 'kalendar.grp.view', order: -10, toggle: true, active: view === 'agenda', onClick: () => setView('agenda') },
    // karta Zobrazení (jako v Souborech): panel a nastavení zobrazení
    { id: 'layers', label: t('kalendar.side'), icon: 'sidebar', tab: 'view', group: 'core.grp.view', toggle: true, active: !sideHidden, onClick: () => setSide(!sideHidden) },
    { id: 'view_settings', label: t('kalendar.cmd.settings'), icon: 'clock', tab: 'view', group: 'core.grp.view', onClick: () => void openSettings() },
    // karta Kalendáře: správa, data, synchronizace (pravidlo kap. 24: Domů = záznamy, druhá karta = kontejner)
    { id: 'calendars', label: t('kalendar.cmd.manage_cals'), icon: 'settings', tab: 'kalendar.tab.kalendare', group: 'kalendar.grp.cals', order: 0, onClick: () => void wm.open({ app: 'kalendar', window: 'kalendare' }) },
    { id: 'new_cal', label: t('kalendar.cmd.new_cal'), icon: 'plus', tab: 'kalendar.tab.kalendare', group: 'kalendar.grp.cals', order: 0, onClick: () => void wm.open({ app: 'kalendar', window: 'kalendar_zaznam', modal: true, parent: win }) },
    { id: 'import', label: t('kalendar.cmd.import'), icon: 'import', tab: 'kalendar.tab.kalendare', group: 'kalendar.grp.data', order: 1, onClick: () => void wm.open({ app: 'kalendar', window: 'import', modal: true, parent: win }) },
    { id: 'export', label: t('kalendar.cmd.export'), icon: 'export', tab: 'kalendar.tab.kalendare', group: 'kalendar.grp.data', order: 1, disabled: !calendars.length, menu: calendars.map((c) => ({ label: c.name, icon: 'calendar', onSelect: () => void exportCal(c.id) })) },
    { id: 'refresh_sub', label: t('kalendar.cmd.refresh_sub'), icon: 'refresh', tab: 'kalendar.tab.kalendare', group: 'kalendar.grp.data', order: 1, disabled: !calendars.some((c) => c.kind === 'subscription'), onClick: () => void refreshSubs() },
    { id: 'sync', label: t('kalendar.cmd.sync'), icon: 'devices', tab: 'kalendar.tab.kalendare', group: 'kalendar.grp.sync', order: 2, onClick: () => void wm.open({ app: 'desktop', window: 'hesla_aplikaci', params: { service: 'caldav' } }) },
  ];
  async function exportCal(id: string): Promise<void> {
    try {
      const r = await runCommand('kalendar', 'kalendare', {}, 'export', [id]);
      if (r.download) window.open(r.download, '_blank');
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }
  async function refreshSubs(): Promise<void> {
    try {
      await action(desc.app, desc.window, 'refresh', desc.params, {});
      toast(t('kalendar.refreshed'), 'ok');
      void load();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }
  const viewNames: Record<View, string> = { day: t('kalendar.cmd.day'), week: t('kalendar.cmd.week'), workweek: t('kalendar.cmd.workweek'), month: t('kalendar.cmd.month'), agenda: t('kalendar.cmd.agenda') };
  /** Lišta: Nový termín · pohled ▾ · ⋯ (Dnes ‹ › jsou u nadpisu období) (správa kalendářů, import, export, synchronizace); ⚙ nastavení zobrazení. */
  function barItems(): BarItem[] {
    const all = buttons();
    const pick = (id: string): RibbonButton | undefined => all.find((b) => b.id === id);
    const item = (b: RibbonButton | undefined, place: BarItem['place'], extra: Partial<BarItem> = {}): BarItem[] => (b ? [{ id: b.id, label: b.label, icon: b.icon, shortcut: b.shortcut, disabled: b.disabled, place, onClick: b.onClick, menu: b.menu, ...extra }] : []);
    return [
      ...item(pick('new'), 'primary'),
      ...['calendars', 'new_cal', 'import', 'export', 'refresh_sub', 'sync'].flatMap((id) => item(pick(id), 'more')),
      { id: 'refresh', label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', place: 'more', onClick: () => void load() },
      // pohled pod ⚙ Zobrazení – stejné místo jako Seznam / Tabulka / Dlaždice v Kontaktech
      ...(['day', 'week', 'workweek', 'month', 'agenda'] as View[]).map((v): BarItem => {
        const b = pick('v_' + v);
        return { id: 'v_' + v, label: viewNames[v], icon: b?.icon, place: 'view', toggle: true, active: view === v, onClick: () => setView(v) };
      }),
      ...item(pick('view_settings'), 'view'),
    ];
  }
  function renderHeader(): void {
    if (!bar) return;
    renderBarHeader({
      win, wm, bar, fab, search: hs,
      menu: { label: t('kalendar.side'), onClick: () => setSide(!sideHidden) },
    });
  }
  const refreshRibbon = () => {
    if (!bar) {
      ribbon.setButtons(buttons());
      return;
    }
    bar.setItems(barItems());
    renderHeader();
  };
  ribbon.setMode(wm.currentMode());
  refreshRibbon();

  // ---- navigace
  function setView(v: View): void {
    view = v;
    void savePref('kalendar.view', v);
    refreshRibbon();
    void load();
  }
  function go(d: Date): void {
    cursor = startOfDay(d);
    void load();
  }
  function step(dir: number): void {
    if (view === 'day') go(addDays(cursor, dir));
    else if (view === 'week' || view === 'workweek') go(addDays(cursor, 7 * dir));
    else if (view === 'month') go(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
    else go(addDays(cursor, 30 * dir));
  }
  function range(): [Date, Date] {
    if (view === 'day') return [cursor, addDays(cursor, 1)];
    if (view === 'week' || view === 'workweek') return [sow(cursor), addDays(sow(cursor), 7)];
    if (view === 'month') {
      const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const start = sow(first);
      return [start, addDays(start, 42)];
    }
    return [cursor, addDays(cursor, 30)];
  }
  /** Dny zobrazené v mřížce: den / celý týden / jen pracovní dny (když žádné nejsou, celý týden). */
  function gridDays(): Date[] {
    if (view === 'day') return [cursor];
    const all = Array.from({ length: 7 }, (_, i) => addDays(sow(cursor), i));
    if (view !== 'workweek') return all;
    const work = all.filter((d) => cfg.workDays.includes(d.getDay()));
    return work.length ? work : all;
  }

  // ---- nastavení zobrazení (první den týdne, pracovní dny, pracovní doba) – předvolby uživatele
  async function openSettings(): Promise<void> {
    const c = settings();
    const firstSel = h('select', { class: 'input sm' }, [1, 6, 0].map((d) => h('option', { value: String(d), selected: d === c.firstDay }, ucfirst(fmt(weekdayDate(d), { weekday: 'long' })))));
    const dayBoxes: HTMLInputElement[] = [];
    const days = h('div', { class: 'cal-settings-days' });
    for (let i = 0; i < 7; i++) {
      const dow = (c.firstDay + i) % 7;
      const cb = h('input', { type: 'checkbox', value: String(dow), checked: c.workDays.includes(dow) });
      dayBoxes.push(cb);
      days.appendChild(h('label', { class: 'check' }, cb, h('span', null, ucfirst(fmt(weekdayDate(dow), { weekday: 'short' })))));
    }
    const hourOpt = (from: number, to: number, sel: number) => Array.from({ length: to - from + 1 }, (_, i) => h('option', { value: String(from + i), selected: from + i === sel }, `${from + i}:00`));
    const fromSel = h('select', { class: 'input sm' }, hourOpt(0, 23, c.workStart));
    const toSel = h('select', { class: 'input sm' }, hourOpt(1, 24, c.workEnd));
    const form = h('div', { class: 'cal-settings' },
      h('div', { class: 'cal-settings-row' }, h('span', { class: 'cal-settings-label' }, t('kalendar.settings.first_day')), firstSel),
      h('div', { class: 'cal-settings-row' }, h('span', { class: 'cal-settings-label' }, t('kalendar.settings.work_days')), days),
      h('div', { class: 'cal-settings-row' }, h('span', { class: 'cal-settings-label' }, t('kalendar.settings.work_hours')), h('span', { class: 'cal-settings-hours' }, t('kalendar.settings.from'), ' ', fromSel, ' ', t('kalendar.settings.to'), ' ', toSel)),
      h('p', { class: 'muted small' }, t('kalendar.settings.hint')));
    const r = await dialog({ title: t('kalendar.settings.title'), message: form, icon: 'clock', width: 460, buttons: [{ id: 'ok', label: t('core.ui.ok'), primary: true }, { id: 'cancel', label: t('core.ui.cancel') }] });
    if (r.id !== 'ok') return;
    const ws = Number(fromSel.value);
    const we = Math.max(ws + 1, Number(toSel.value));
    await Promise.all([
      savePref('kalendar.firstDay', Number(firstSel.value)),
      savePref('kalendar.workDays', dayBoxes.filter((b) => b.checked).map((b) => Number(b.value))),
      savePref('kalendar.workStart', ws),
      savePref('kalendar.workEnd', we),
    ]);
    cfg = settings();
    void load();
  }

  // ---- data
  const reload = debounce(() => void load(), 300);
  async function load(): Promise<void> {
    [rangeFrom, rangeTo] = range();
    try {
      const shown = calendars.filter((c) => !hidden.has(c.id)).map((c) => c.id);
      const r = searchQ
        ? await action<{ events: Occ[] }>(desc.app, desc.window, 'search', desc.params, { q: searchQ, calendars: shown })
        : await action<{ events: Occ[] }>(desc.app, desc.window, 'range', desc.params, { from: rangeFrom.toISOString(), to: rangeTo.toISOString(), calendars: shown, birthdays });
      occs = r.events.map((o) => ({ ...o, s: new Date(o.start), e: new Date(o.end) }));
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
      occs = [];
    }
    render();
  }
  async function refreshCalendars(): Promise<void> {
    try {
      const d = await action<{ payload: Payload }>(desc.app, desc.window, 'describe', desc.params, {});
      calendars = d.payload.calendars;
      p.defaultCalendar = d.payload.defaultCalendar;
      renderSide();
      refreshRibbon();
    } catch {
      /* ignore */
    }
  }
  // seznam kalendářů i výchozí (poslední použitý) kalendář se obnovují s každou změnou a po resetu spojení
  // (po resetu se dřív obnovily jen termíny a nově založený kalendář v panelu chyběl)
  const unsub = events.subscribe(['kalendar.udalosti', 'kalendar.kalendare'], () => void refreshCalendars().then(() => reload()), { onReset: () => void refreshCalendars().then(() => reload()) });

  // ---- akce
  function openNew(start?: Date, end?: Date, allDay = false): void {
    const cal = writableCal();
    if (!cal) return;
    const s = start ?? nextHour();
    const e = end ?? new Date(s.getTime() + 3600000);
    void wm.open({ app: 'kalendar', window: 'udalost', params: { start: s.toISOString(), end: e.toISOString(), allDay, calendar: cal }, modal: true, parent: win });
  }
  function nextHour(): Date {
    const d = new Date(cursor);
    const now = new Date();
    d.setHours(sameDay(cursor, now) ? now.getHours() + 1 : 9, 0, 0, 0);
    return d;
  }
  const askable = (o: Occ): boolean => o.recurring && !o.exception && o.instanceStart !== null && o.editable;
  /** Opakovaný termín: jen tento výskyt / tento a následující / celou řadu (jako Outlook). null = zrušeno. */
  async function askScope(o: Occ, del: boolean): Promise<string | null> {
    if (!askable(o)) return 'all';
    const body = h('div', { class: 'cal-scope' }, ...(['this', 'following', 'all'] as const).map((s) => h('label', { class: 'check' }, h('input', { type: 'radio', name: 'cal-scope', value: s }), h('span', null, t('kalendar.scope.' + s)))));
    const first = body.querySelector<HTMLInputElement>('input');
    if (first) first.checked = true;
    const r = await dialog({ title: t(del ? 'kalendar.scope.delete_title' : 'kalendar.scope.edit_title', { name: o.summary }), message: body, buttons: [{ id: 'cancel', label: t('core.ui.cancel') }, { id: 'ok', label: t(del ? 'core.cmd.delete' : 'core.ui.ok'), primary: true, danger: del }] });
    if (r.id !== 'ok') return null;
    return body.querySelector<HTMLInputElement>('input:checked')?.value ?? 'this';
  }
  const scopeParams = (o: Occ, scope: string): Record<string, string> => (scope !== 'all' && o.instanceStart ? { instance: o.instanceStart, scope } : {});
  async function openOcc(o: Occ): Promise<void> {
    if (o.calendarId === 'birthdays') {
      if (o.contactId) void wm.open({ app: 'kontakty', window: 'kontakt', params: { id: o.contactId }, modal: true, parent: win });
      return;
    }
    const scope = await askScope(o, false);
    if (scope === null) return;
    void wm.open({ app: 'kalendar', window: 'udalost', params: { id: o.id, ...scopeParams(o, scope) }, modal: true, parent: win });
  }
  async function quickCreate(start: Date, end: Date, allDay: boolean): Promise<void> {
    const cal = writableCal();
    if (!cal) return;
    const title = await prompt(t('kalendar.quick_prompt'), '', { title: t('kalendar.quick_title') });
    if (title === null) return;
    try {
      await action(desc.app, desc.window, 'quick', desc.params, { calendar: cal, summary: title.trim() || t('kalendar.untitled'), start: start.toISOString(), end: end.toISOString(), allDay });
      events.kick();
      void load();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }
  async function moveOcc(o: Occ, start: Date, end: Date, allDay: boolean): Promise<void> {
    try {
      await action(desc.app, desc.window, 'move', desc.params, { id: o.id, start: start.toISOString(), end: end.toISOString(), allDay, instanceStart: o.recurring && !o.exception ? o.instanceStart : null });
      events.kick();
      void load();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
      render();
    }
  }
  /** Přesun termínu do jiného kalendáře (přetažení na kalendář v panelu, nabídka „Přesunout do…“). */
  async function moveToCal(o: Occ, calId: string): Promise<void> {
    const target = calendars.find((c) => c.id === calId);
    if (!target || calId === o.calendarId) return;
    try {
      await action(desc.app, desc.window, 'move_cal', desc.params, { id: o.id, calendar: calId });
      toast(t('core.ui.moved_to', { name: target.name, count: 1 }), 'ok');
      events.kick();
      void load();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }
  /** Termín právě přetahovaný myší (měsíc) – cíl může být i kalendář v bočním panelu. */
  let dragOcc: Occ | null = null;
  async function deleteOcc(o: Occ): Promise<void> {
    const scope = await askScope(o, true); // u opakovaného se ptá rovnou „co smazat“ (to je i potvrzení)
    if (scope === null) return;
    if (!askable(o) && !(await confirm(t('kalendar.delete_q', { name: o.summary }), { danger: true, okLabel: t('core.cmd.delete') }))) return;
    try {
      await action(desc.app, 'udalost', 'delete', { id: o.id, ...scopeParams(o, scope) }, {});
      events.kick();
      void load();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }
  function occMenu(o: Occ): MenuItem[] {
    const m: MenuItem[] = [{ label: t('core.cmd.open'), icon: 'edit', onSelect: () => openOcc(o) }];
    if (o.editable && !o.contactId) {
      const targets = calendars.filter((c) => c.eventsWritable && c.id !== o.calendarId);
      if (targets.length) m.push({ label: t('core.ui.move_to'), icon: 'calendar', children: targets.map((c) => ({ label: c.name, onSelect: () => void moveToCal(o, c.id) })) });
    }
    if (o.editable) m.push({ label: t('core.cmd.delete'), icon: 'trash', danger: true, onSelect: () => void deleteOcc(o) });
    m.push({ separator: true, label: '' }, { label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', onSelect: () => void load() });
    return m;
  }

  // ---- boční panel: mini měsíc + kalendáře
  let miniMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  function renderSide(): void {
    clear(side);
    const mini = h('div', { class: 'cal-mini' });
    const mhead = h('div', { class: 'cal-mini-head' });
    const prevB = h('button', { type: 'button', class: 'btn ghost sm' }, icon('chevron-left'));
    const nextB = h('button', { type: 'button', class: 'btn ghost sm' }, icon('chevron'));
    prevB.addEventListener('click', () => { miniMonth = new Date(miniMonth.getFullYear(), miniMonth.getMonth() - 1, 1); renderSide(); });
    nextB.addEventListener('click', () => { miniMonth = new Date(miniMonth.getFullYear(), miniMonth.getMonth() + 1, 1); renderSide(); });
    mhead.append(prevB, h('span', { class: 'cal-mini-title' }, fmt(miniMonth, { month: 'long', year: 'numeric' })), nextB);
    const grid = h('div', { class: 'cal-mini-grid' });
    const start = sow(miniMonth);
    for (let i = 0; i < 7; i++) grid.appendChild(h('span', { class: 'cal-mini-dow' }, fmt(addDays(start, i), { weekday: 'narrow' })));
    const today = new Date();
    const [rf, rt] = range();
    for (let i = 0; i < 42; i++) {
      const d = addDays(start, i);
      const b = h('button', { type: 'button', class: 'cal-mini-day' + (d.getMonth() !== miniMonth.getMonth() ? ' other' : '') + (sameDay(d, today) ? ' today' : '') + (d >= rf && d < rt ? ' in-range' : '') }, String(d.getDate()));
      b.addEventListener('click', () => { go(d); if (narrow) setSide(true); }); // úzké okno / telefon: výběr dne panel zavře
      grid.appendChild(b);
    }
    mini.append(mhead, grid);
    const list = h('div', { class: 'cal-cals' }, h('h3', null, t('kalendar.layers')));
    for (const c of calendars) {
      const cb = h('input', { type: 'checkbox', checked: !hidden.has(c.id) });
      cb.addEventListener('change', () => {
        if (cb.checked) hidden.delete(c.id);
        else hidden.add(c.id);
        void savePref('kalendar.hidden', Array.from(hidden));
        void load();
      });
      const label = h('label', { class: 'cal-cal' }, cb, h('span', { class: 'cal-dot', style: { background: c.color } }), h('span', { class: 'cal-cal-name' }, c.name), !c.eventsWritable ? h('span', { class: 'muted small' }, t('kalendar.read_only')) : null);
      if (c.eventsWritable) {
        // cíl přetažení termínu z měsíčního pohledu (stejně jako vrstvy v Seznamu)
        label.addEventListener('dragover', (e) => { if (dragOcc && dragOcc.calendarId !== c.id) { e.preventDefault(); label.classList.add('drop'); } });
        label.addEventListener('dragleave', () => label.classList.remove('drop'));
        label.addEventListener('drop', (e) => { e.preventDefault(); label.classList.remove('drop'); if (dragOcc) void moveToCal(dragOcc, c.id); dragOcc = null; });
      }
      list.appendChild(label);
    }
    if (p.birthdays) {
      const cb = h('input', { type: 'checkbox', checked: birthdays });
      cb.addEventListener('change', () => {
        birthdays = cb.checked;
        void savePref('kalendar.birthdays', birthdays);
        void load();
      });
      list.appendChild(h('label', { class: 'cal-cal' }, cb, h('span', { class: 'cal-dot', style: { background: '#d6336c' } }), h('span', { class: 'cal-cal-name' }, t('kalendar.cmd.birthdays'))));
    }
    side.append(mini, list);
  }

  // ---- vykreslení
  /** posun mřížky dne / týdne – obnovení (živá změna) ho zachová */
  let gridKey = '';
  let gridTop = -1;
  function render(): void {
    miniMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    renderSide();
    const sc = body.querySelector<HTMLElement>('.cal-scroll');
    if (sc) gridTop = sc.scrollTop;
    clear(head);
    clear(body);
    if (searchQ) {
      head.appendChild(h('h2', { class: 'cal-title' }, t('kalendar.search_title', { q: searchQ })));
      renderSearch();
      return;
    }
    if (bar) {
      // navigace u nadpisu období (vzor Outlook): Dnes ‹ › – vidět i v úzkém okně a na telefonu
      const nav = (ic: string, label: string, fn: () => void, text = false) => {
        const b = h('button', { type: 'button', class: 'btn ghost', title: label, 'aria-label': label }, icon(ic), text ? label : null);
        b.addEventListener('click', fn);
        return b;
      };
      head.classList.add('cal-head-nav');
      head.append(nav('calendar', t('kalendar.cmd.today'), () => go(startOfDay(new Date())), true), nav('chevron-left', t('kalendar.cmd.prev'), () => step(-1)), nav('chevron', t('kalendar.cmd.next'), () => step(1)));
    }
    head.appendChild(h('h2', { class: 'cal-title' }, ucfirst(periodTitle(isMobile()))));
    if (view === 'month') renderMonth();
    else if (view === 'agenda') renderAgenda();
    else renderGrid(gridDays());
    win.setTitle(bar ? t('kalendar.name') : `${t('kalendar.name')} – ${periodTitle()}`, 'calendar');
  }
  /** Nadpis období; short = telefon (musí se vejít vedle Dnes ‹ ›). */
  function periodTitle(short = false): string {
    if (short && view === 'day') return fmt(cursor, { weekday: 'short', day: 'numeric', month: 'short' });
    if (short && view !== 'month') {
      const a = range()[0];
      const last = addDays(range()[1], -1);
      return `${fmt(a, { day: 'numeric', month: 'numeric' })} – ${fmt(last, { day: 'numeric', month: 'numeric' })}`;
    }
    if (view === 'day') return fmt(cursor, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (view === 'month') return fmt(cursor, { month: 'long', year: 'numeric' });
    const days = view === 'agenda' ? null : gridDays();
    const a = (days ? days[0] : range()[0]) ?? cursor;
    const last = (days ? days[days.length - 1] : addDays(range()[1], -1)) ?? cursor;
    if (view === 'agenda') return `${fmt(a, { day: 'numeric', month: 'long' })} – ${fmt(last, { day: 'numeric', month: 'long', year: 'numeric' })}`;
    return a.getMonth() === last.getMonth() ? `${a.getDate()}.–${fmt(last, { day: 'numeric', month: 'long', year: 'numeric' })}` : `${fmt(a, { day: 'numeric', month: 'short' })} – ${fmt(last, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  /** Termín jde vybrat tabulátorem a otevřít Enterem (jako řádek seznamu); Delete smaže, Shift+F10 nabídka. */
  function keyable(el: HTMLElement, o: Occ): void {
    el.tabIndex = 0;
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); void openOcc(o); }
      else if (e.key === 'Delete' && o.editable) { e.preventDefault(); e.stopPropagation(); void deleteOcc(o); }
    });
  }
  function chipStyle(o: Occ): Record<string, string> {
    return { '--ev': o.color } as Record<string, string>;
  }
  function occsOnDay(d: Date): { allDay: Occ[]; timed: Occ[] } {
    const ds = d.getTime();
    const de = ds + DAY_MS;
    const allDay: Occ[] = [];
    const timed: Occ[] = [];
    for (const o of occs) {
      if (o.e.getTime() <= ds || o.s.getTime() >= de) continue;
      if (o.allDay || o.e.getTime() - o.s.getTime() >= DAY_MS) allDay.push(o);
      else timed.push(o);
    }
    return { allDay, timed };
  }

  // -- den / týden
  /** Vícedenní a celodenní události přes zadané dny → pruhy (sloupec od/do, dráha). Dny nemusí jít po sobě (pracovní týden). */
  function spanLanes(dayList: Date[]): { o: Occ; c0: number; c1: number; lane: number }[] {
    const first = dayList[0] ?? cursor;
    const endAll = addDays(dayList[dayList.length - 1] ?? first, 1);
    const multi = occs.filter((o) => o.allDay || o.e.getTime() - o.s.getTime() >= DAY_MS).filter((o) => o.e > first && o.s < endAll).sort((a, b) => a.s.getTime() - b.s.getTime() || b.e.getTime() - a.e.getTime());
    const laneEnd: number[] = [];
    const out: { o: Occ; c0: number; c1: number; lane: number }[] = [];
    for (const o of multi) {
      let c0 = dayList.findIndex((d) => addDays(d, 1) > o.s);
      let c1 = dayList.length - 1;
      while (c1 >= 0 && (dayList[c1] ?? first) >= o.e) c1--;
      if (c0 < 0 || c1 < c0) continue; // leží jen ve skrytých dnech
      c1 += 1;
      c0 = Math.max(0, c0);
      let lane = laneEnd.findIndex((end) => end <= c0);
      if (lane < 0) { lane = laneEnd.length; laneEnd.push(0); }
      laneEnd[lane] = c1;
      out.push({ o, c0, c1, lane });
    }
    return out;
  }
  function spanChip(o: Occ, extra: Record<string, string>): HTMLElement {
    const chip = h('button', { type: 'button', class: 'cal-chip' + (o.editable ? '' : ' ro') + (o.status === 'cancelled' ? ' cancelled' : '') + (o.status === 'tentative' ? ' tentative' : ''), style: { ...chipStyle(o), ...extra }, title: o.summary }, h('span', { class: 'cal-chip-title' }, o.summary));
    chip.addEventListener('click', () => openOcc(o));
    contextMenu(chip, () => occMenu(o));
    return chip;
  }
  function renderGrid(dayList: Date[]): void {
    const days = dayList.length;
    const today = new Date();
    const cols = `56px repeat(${days}, minmax(0, 1fr))`;
    const header = h('div', { class: 'cal-days', style: { gridTemplateColumns: cols } }, h('div'));
    for (const d of dayList) {
      const b = h('button', { type: 'button', class: 'cal-dayhead' + (sameDay(d, today) ? ' today' : '') + (cfg.workDays.includes(d.getDay()) ? '' : ' off') }, h('span', { class: 'dow' }, fmt(d, { weekday: 'short' })), h('span', { class: 'num' }, String(d.getDate())));
      b.addEventListener('click', () => { cursor = d; setView('day'); });
      header.appendChild(b);
    }
    const allday = h('div', { class: 'cal-allday', style: { gridTemplateColumns: cols } }, h('div', { class: 'cal-gutter-label' }, t('kalendar.all_day_row')));
    const lanes = h('div', { class: 'cal-allday-lanes', style: { gridColumn: `2 / span ${days}`, gridTemplateColumns: `repeat(${days}, minmax(0, 1fr))` } });
    for (const { o, c0, c1, lane } of spanLanes(dayList)) lanes.appendChild(spanChip(o, { gridColumn: `${c0 + 1} / ${c1 + 1}`, gridRow: String(lane + 1) }));
    allday.appendChild(lanes);
    const scroll = h('div', { class: 'cal-scroll' });
    const grid = h('div', { class: 'cal-grid', style: { gridTemplateColumns: cols, height: HOUR_PX * 24 + 'px' } });
    const gutter = h('div', { class: 'cal-gutter' });
    for (let hr = 0; hr < 24; hr++) gutter.appendChild(h('div', { class: 'cal-hour-label', style: { top: hr * HOUR_PX + 'px' } }, hr ? `${hr}:00` : ''));
    grid.appendChild(gutter);
    dayList.forEach((d, di) => {
      const workDay = cfg.workDays.includes(d.getDay());
      const col = h('div', { class: 'cal-col' + (sameDay(d, today) ? ' today' : '') + (workDay ? '' : ' off-day'), dataset: { day: String(di) } });
      if (workDay) {
        // mimo pracovní dobu šedě
        if (cfg.workStart > 0) col.appendChild(h('div', { class: 'cal-off', style: { top: '0', height: cfg.workStart * HOUR_PX + 'px' } }));
        if (cfg.workEnd < 24) col.appendChild(h('div', { class: 'cal-off', style: { top: cfg.workEnd * HOUR_PX + 'px', height: (24 - cfg.workEnd) * HOUR_PX + 'px' } }));
      }
      for (let hr = 0; hr < 24; hr++) col.appendChild(h('div', { class: 'cal-hour', style: { top: hr * HOUR_PX + 'px' } }));
      const { timed } = occsOnDay(d);
      layoutTimed(timed, d).forEach(({ o, top, height, left, width }) => {
        const ev = h('div', { class: 'cal-ev' + (o.editable ? '' : ' ro') + (o.status === 'cancelled' ? ' cancelled' : '') + (o.status === 'tentative' ? ' tentative' : ''), style: { ...chipStyle(o), top: top + 'px', height: Math.max(18, height) + 'px', left: left + '%', width: width + '%' }, title: `${fmtTime(o.s)}–${fmtTime(o.e)} ${o.summary}` },
          h('div', { class: 'cal-ev-time' }, `${fmtTime(o.s)}–${fmtTime(o.e)}`), h('div', { class: 'cal-ev-title' }, o.summary), o.location ? h('div', { class: 'cal-ev-loc' }, o.location) : null,
          o.editable ? h('div', { class: 'cal-ev-resize' }) : null);
        wireDrag(ev, o, d, dayList, grid);
        keyable(ev, o);
        contextMenu(ev, () => occMenu(o));
        col.appendChild(ev);
      });
      if (sameDay(d, today)) col.appendChild(h('div', { class: 'cal-now', style: { top: (minutesOf(today) / 60) * HOUR_PX + 'px' } }));
      wireCreate(col, d);
      contextMenu(col, (e) => {
        if ((e.target as HTMLElement).closest('.cal-ev')) return null;
        const m = Math.floor(minuteAt(col, e.clientY) / 60) * 60;
        return [
          { label: t('kalendar.cmd.new'), icon: 'plus', disabled: !writableCal(), onSelect: () => openNew(new Date(d.getTime() + m * 60000), new Date(d.getTime() + (m + 60) * 60000)) },
          { separator: true, label: '' },
          { label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', onSelect: () => void load() },
        ];
      });
      grid.appendChild(col);
    });
    scroll.appendChild(grid);
    body.append(header, allday, scroll);
    const key = view + ymd(dayList[0] ?? cursor);
    const top = key === gridKey && gridTop >= 0 ? gridTop : Math.max(0, cfg.workStart - 1) * HOUR_PX; // stejné období: místo zůstane
    gridKey = key;
    requestAnimationFrame(() => { scroll.scrollTop = top; });
  }
  function layoutTimed(list: Occ[], day: Date): { o: Occ; top: number; height: number; left: number; width: number }[] {
    const ds = day.getTime();
    const items = list.map((o) => ({ o, s: Math.max(0, (o.s.getTime() - ds) / 60000), e: Math.min(1440, Math.max((o.e.getTime() - ds) / 60000, (o.s.getTime() - ds) / 60000 + 15)), col: 0, ncol: 1 }));
    items.sort((a, b) => a.s - b.s || b.e - a.e);
    let cluster: typeof items = [];
    let colEnds: number[] = [];
    let clusterEnd = -1;
    const flush = () => { for (const it of cluster) it.ncol = colEnds.length; cluster = []; colEnds = []; };
    for (const it of items) {
      if (it.s >= clusterEnd) flush();
      let c = colEnds.findIndex((end) => end <= it.s);
      if (c < 0) { c = colEnds.length; colEnds.push(0); }
      colEnds[c] = it.e;
      it.col = c;
      cluster.push(it);
      clusterEnd = Math.max(clusterEnd, it.e);
    }
    flush();
    return items.map((it) => ({ o: it.o, top: (it.s / 60) * HOUR_PX, height: ((it.e - it.s) / 60) * HOUR_PX - 2, left: (100 / it.ncol) * it.col, width: 100 / it.ncol - 1 }));
  }
  function minuteAt(col: HTMLElement, clientY: number): number {
    const r = col.getBoundingClientRect();
    return clamp(Math.round(((clientY - r.top) / HOUR_PX) * 60 / SNAP) * SNAP, 0, 1440);
  }
  function wireCreate(col: HTMLElement, day: Date): void {
    let sel: HTMLElement | null = null;
    let m0 = 0;
    col.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || (e.target as HTMLElement).closest('.cal-ev')) return;
      if (e.pointerType === 'touch') return;
      m0 = minuteAt(col, e.clientY);
      col.setPointerCapture(e.pointerId);
      sel = h('div', { class: 'cal-sel' });
      col.appendChild(sel);
      paint(m0, m0 + SNAP);
      const move = (ev: PointerEvent) => { const m1 = minuteAt(col, ev.clientY); paint(Math.min(m0, m1), Math.max(m0, m1, Math.min(m0, m1) + SNAP)); };
      const up = (ev: PointerEvent) => {
        col.removeEventListener('pointermove', move);
        col.removeEventListener('pointerup', up);
        const m1 = minuteAt(col, ev.clientY);
        const a = Math.min(m0, m1);
        const b = Math.max(m0, m1, a + SNAP);
        sel?.remove();
        sel = null;
        if (Math.abs(m1 - m0) >= SNAP) void quickCreate(new Date(day.getTime() + a * 60000), new Date(day.getTime() + b * 60000), false);
      };
      col.addEventListener('pointermove', move);
      col.addEventListener('pointerup', up);
    });
    col.addEventListener('dblclick', (e) => { // dvojklik = plné okno Termín; rychlý dialog zůstává jen pro tažení
      if ((e.target as HTMLElement).closest('.cal-ev')) return;
      const m = Math.floor(minuteAt(col, e.clientY) / 60) * 60;
      openNew(new Date(day.getTime() + m * 60000), new Date(day.getTime() + (m + 60) * 60000), false);
    });
    function paint(a: number, b: number): void {
      if (!sel) return;
      sel.style.top = (a / 60) * HOUR_PX + 'px';
      sel.style.height = ((b - a) / 60) * HOUR_PX + 'px';
      sel.textContent = `${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}–${String(Math.floor(b / 60)).padStart(2, '0')}:${String(b % 60).padStart(2, '0')}`;
    }
  }
  function wireDrag(ev: HTMLElement, o: Occ, day: Date, dayList: Date[], grid: HTMLElement): void {
    let moved = false;
    ev.addEventListener('click', () => { if (!moved) openOcc(o); moved = false; });
    if (!o.editable) return;
    ev.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || e.pointerType === 'touch') return;
      e.stopPropagation();
      const resize = (e.target as HTMLElement).classList.contains('cal-ev-resize');
      const y0 = e.clientY;
      const x0 = e.clientX;
      const startMin = minutesOf(o.s) + (startOfDay(o.s).getTime() - day.getTime()) / 60000;
      const dur = (o.e.getTime() - o.s.getTime()) / 60000;
      const colW = (grid.getBoundingClientRect().width - 56) / dayList.length;
      let dMin = 0;
      let dDay = 0;
      ev.setPointerCapture(e.pointerId);
      ev.classList.add('dragging');
      const move = (m: PointerEvent) => {
        dMin = Math.round(((m.clientY - y0) / HOUR_PX) * 60 / SNAP) * SNAP;
        dDay = resize ? 0 : clamp(Math.round((m.clientX - x0) / colW), -dayList.indexOf(day), dayList.length - 1 - dayList.indexOf(day));
        if (Math.abs(m.clientY - y0) > 4 || dDay !== 0) moved = true;
        if (resize) ev.style.height = Math.max(18, ((Math.max(SNAP, dur + dMin)) / 60) * HOUR_PX - 2) + 'px';
        else {
          ev.style.top = ((startMin + dMin) / 60) * HOUR_PX + 'px';
          ev.style.transform = dDay ? `translateX(${dDay * 100}%)` : '';
        }
      };
      const up = () => {
        ev.removeEventListener('pointermove', move);
        ev.removeEventListener('pointerup', up);
        ev.classList.remove('dragging');
        if (!moved) return;
        if (resize) {
          const newDur = Math.max(SNAP, dur + dMin);
          void moveOcc(o, o.s, new Date(o.s.getTime() + newDur * 60000), false);
        } else {
          const target = dayList[dayList.indexOf(day) + dDay] ?? day; // dny v mřížce nemusí jít po sobě
          const ns = new Date(o.s.getTime() + dMin * 60000 + (target.getTime() - day.getTime()));
          void moveOcc(o, ns, new Date(ns.getTime() + dur * 60000), false);
        }
      };
      ev.addEventListener('pointermove', move);
      ev.addEventListener('pointerup', up);
    });
  }

  // -- měsíc
  function renderMonth(): void {
    const [start] = range();
    const today = new Date();
    const grid = h('div', { class: 'cal-month' });
    for (let i = 0; i < 7; i++) grid.appendChild(h('div', { class: 'cal-month-dow' + (cfg.workDays.includes(addDays(start, i).getDay()) ? '' : ' off') }, fmt(addDays(start, i), { weekday: 'short' })));
    let dragging: { o: Occ; from: Date } | null = null;
    const wireDnd = (chip: HTMLElement, o: Occ, from: () => Date) => {
      if (!o.editable) return;
      chip.draggable = true;
      chip.addEventListener('dragstart', (e) => { dragging = { o, from: from() }; dragOcc = o.contactId ? null : o; e.dataTransfer?.setData('text/plain', o.summary); if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; });
      chip.addEventListener('dragend', () => { dragging = null; dragOcc = null; grid.querySelectorAll('.cal-cell.drop').forEach((c) => c.classList.remove('drop')); side.querySelectorAll('.cal-cal.drop').forEach((c) => c.classList.remove('drop')); });
    };
    const maxLanes = 3;
    for (let w = 0; w < 6; w++) {
      const weekDays = Array.from({ length: 7 }, (_, i) => addDays(start, w * 7 + i));
      const row = h('div', { class: 'cal-week' });
      const spans = spanLanes(weekDays);
      const laneCount = Math.min(maxLanes, spans.reduce((m, s) => Math.max(m, s.lane + 1), 0));
      const hiddenPerDay = new Array<number>(7).fill(0); // pruhy nad limit drah → do „+N další“
      const lanes = h('div', { class: 'cal-month-lanes' });
      for (const { o, c0, c1, lane } of spans) {
        if (lane >= maxLanes) { for (let i = c0; i < c1; i++) hiddenPerDay[i] = (hiddenPerDay[i] ?? 0) + 1; continue; }
        const chip = spanChip(o, { gridColumn: `${c0 + 1} / ${c1 + 1}`, gridRow: String(lane + 1) });
        wireDnd(chip, o, () => startOfDay(new Date(Math.max(o.s.getTime(), (weekDays[c0] ?? o.s).getTime()))));
        lanes.appendChild(chip);
      }
      weekDays.forEach((d, i) => {
        const cell = h('div', { class: 'cal-cell' + (d.getMonth() !== cursor.getMonth() ? ' other' : '') + (sameDay(d, today) ? ' today' : '') + (cfg.workDays.includes(d.getDay()) ? '' : ' off-day'), dataset: { day: ymd(d) } });
        const num = h('button', { type: 'button', class: 'cal-cell-num' }, String(d.getDate()));
        num.addEventListener('click', () => { cursor = d; setView('day'); });
        cell.append(num, h('div', { class: 'cal-cell-lanes', style: { height: laneCount * LANE_PX + 'px' } }));
        const timed = occsOnDay(d).timed.sort((a, b) => a.s.getTime() - b.s.getTime());
        const max = Math.max(1, 4 - laneCount);
        timed.slice(0, max).forEach((o) => {
          const chip = h('div', { class: 'cal-chip timed' + (o.editable ? '' : ' ro') + (o.status === 'cancelled' ? ' cancelled' : '') + (o.status === 'tentative' ? ' tentative' : ''), style: chipStyle(o), title: `${fmtTime(o.s)} ${o.summary}` }, h('span', { class: 'cal-chip-time' }, fmtTime(o.s)), h('span', { class: 'cal-chip-title' }, o.summary));
          chip.addEventListener('click', () => openOcc(o));
          keyable(chip, o);
          contextMenu(chip, () => occMenu(o));
          wireDnd(chip, o, () => d);
          cell.appendChild(chip);
        });
        const hiddenCount = Math.max(0, timed.length - max) + (hiddenPerDay[i] ?? 0);
        if (hiddenCount > 0) {
          const more = h('button', { type: 'button', class: 'cal-more' }, t('kalendar.more', { count: hiddenCount }));
          more.addEventListener('click', () => { cursor = d; setView('day'); });
          cell.appendChild(more);
        }
        cell.addEventListener('dragover', (e) => { if (dragging) { e.preventDefault(); cell.classList.add('drop'); } });
        cell.addEventListener('dragleave', () => cell.classList.remove('drop'));
        cell.addEventListener('drop', (e) => {
          e.preventDefault();
          cell.classList.remove('drop');
          if (!dragging) return;
          const delta = Math.round((d.getTime() - dragging.from.getTime()) / DAY_MS);
          if (delta !== 0) {
            const o = dragging.o;
            void moveOcc(o, new Date(o.s.getTime() + delta * DAY_MS), new Date(o.e.getTime() + delta * DAY_MS), o.allDay);
          }
          dragging = null;
        });
        cell.addEventListener('dblclick', (e) => { if (!(e.target as HTMLElement).closest('.cal-chip')) openNew(d, addDays(d, 1), true); }); // plné okno, celodenní
        contextMenu(cell, (e) => ((e.target as HTMLElement).closest('.cal-chip') ? null : [{ label: t('kalendar.cmd.new'), icon: 'plus', disabled: !writableCal(), onSelect: () => openNew(new Date(d.getTime() + 9 * 3600000), new Date(d.getTime() + 10 * 3600000)) }, { separator: true, label: '' }, { label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', onSelect: () => void load() }]));
        row.appendChild(cell);
      });
      row.appendChild(lanes);
      grid.appendChild(row);
    }
    body.appendChild(grid);
  }

  // -- agenda
  function renderAgenda(): void {
    const [start, end] = range();
    const wrap = h('div', { class: 'cal-agenda' });
    let any = false;
    for (let d = start; d < end; d = addDays(d, 1)) {
      const { allDay, timed } = occsOnDay(d);
      const list = [...allDay, ...timed.sort((a, b) => a.s.getTime() - b.s.getTime())];
      if (!list.length) continue;
      any = true;
      const day = h('div', { class: 'cal-agenda-day' + (sameDay(d, new Date()) ? ' today' : '') }, h('div', { class: 'cal-agenda-date' }, h('b', null, ucfirst(fmt(d, { weekday: 'long' }))), ' ', fmt(d, { day: 'numeric', month: 'long' })));
      for (const o of list) {
        const row = h('div', { class: 'cal-agenda-row' + (o.status === 'cancelled' ? ' cancelled' : ''), style: chipStyle(o) }, h('span', { class: 'cal-dot' }), h('span', { class: 'cal-agenda-time' }, o.allDay ? t('kalendar.all_day_row') : `${fmtTime(o.s)}–${fmtTime(o.e)}`), h('span', { class: 'cal-agenda-title' }, o.summary, o.location ? h('span', { class: 'muted' }, ` · ${o.location}`) : null));
        row.addEventListener('click', () => openOcc(o));
        keyable(row, o);
        contextMenu(row, () => occMenu(o));
        day.appendChild(row);
      }
      wrap.appendChild(day);
    }
    if (!any) wrap.appendChild(emptyWithActions(t('kalendar.no_events')));
    body.appendChild(wrap);
  }
  /** Prázdný stav s cestou dál (vzor Kontakty): Vymazat hledání / Nový termín. */
  function emptyWithActions(text: string): HTMLElement {
    const st = emptyState(text, 'calendar');
    const acts: HTMLElement[] = [];
    if (searchQ && hs) {
      const b = h('button', { type: 'button', class: 'btn' }, icon('x'), t('core.ui.clear_search'));
      b.addEventListener('click', () => { hs.set(''); if (hs.open) hs.toggle(false); else { searchQ = ''; void load(); } });
      acts.push(b);
    }
    if (writableCal()) {
      const b = h('button', { type: 'button', class: 'btn primary' }, icon('plus'), t('kalendar.cmd.new'));
      b.addEventListener('click', () => openNew());
      acts.push(b);
    }
    if (acts.length) st.appendChild(h('div', { class: 'list-empty-actions' }, acts));
    return st;
  }
  /** Výsledky hledání: termíny po dnech (nadcházející, pak minulé) – klepnutí otevře, nabídka jako v mřížce. */
  function renderSearch(): void {
    const wrap = h('div', { class: 'cal-agenda' });
    let lastDay = '';
    let day: HTMLElement | null = null;
    for (const o of occs) {
      const key = ymd(o.s);
      if (key !== lastDay || !day) {
        lastDay = key;
        day = h('div', { class: 'cal-agenda-day' + (sameDay(o.s, new Date()) ? ' today' : '') }, h('div', { class: 'cal-agenda-date' }, h('b', null, ucfirst(fmt(o.s, { weekday: 'long' }))), ' ', fmt(o.s, { day: 'numeric', month: 'long', year: 'numeric' })));
        wrap.appendChild(day);
      }
      const row = h('div', { class: 'cal-agenda-row' + (o.status === 'cancelled' ? ' cancelled' : ''), style: chipStyle(o) }, h('span', { class: 'cal-dot' }), h('span', { class: 'cal-agenda-time' }, o.allDay ? t('kalendar.all_day_row') : `${fmtTime(o.s)}–${fmtTime(o.e)}`), h('span', { class: 'cal-agenda-title' }, o.summary, o.location ? h('span', { class: 'muted' }, ` · ${o.location}`) : null));
      row.addEventListener('click', () => openOcc(o));
      keyable(row, o);
      contextMenu(row, () => occMenu(o));
      day.appendChild(row);
    }
    if (!occs.length) wrap.appendChild(emptyWithActions(t('core.ui.nothing_found', { q: searchQ })));
    body.appendChild(wrap);
  }

  // ---- připomenutí (každou minutu, každé jen jednou)
  async function checkReminders(): Promise<void> {
    try {
      const r = await action<{ reminders: { key: string; summary: string; start: string; allDay: boolean; minutes: number }[] }>(desc.app, desc.window, 'reminders', desc.params, {});
      for (const rem of r.reminders) {
        if (shownReminders.has(rem.key)) continue;
        shownReminders.add(rem.key);
        const s = new Date(rem.start);
        const diff = Math.round((s.getTime() - Date.now()) / 60000);
        toast(t('kalendar.reminder_toast', { summary: rem.summary, when: rem.allDay ? fmt(s, { day: 'numeric', month: 'short' }) : diff <= 0 ? t('kalendar.now') : t('kalendar.in_minutes', { n: diff }) }), 'info');
      }
    } catch {
      /* ticho */
    }
  }
  const timer = window.setInterval(() => void checkReminders(), 60000);
  void checkReminders();

  void load();
  return {
    el,
    getState: () => ({ view, date: ymd(cursor) }),
    setState: (s) => {
      const st = s as { view?: View; date?: string } | null;
      if (st?.view) view = st.view;
      if (st?.date) cursor = startOfDay(new Date(st.date));
      cfg = settings();
      refreshRibbon();
      void load();
    },
    destroy: () => {
      unsub();
      ro.disconnect();
      window.clearInterval(timer);
    },
    setMode: (m: 'pc' | 'tablet' | 'mobile') => {
      if (bar) { renderHeader(); render(); }
      else ribbon.setMode(m);
    },
    shortcut: (sc: string) => {
      if (sc === 'Ctrl+N') { openNew(); return true; }
      if (sc === 'PageUp') { step(-1); return true; }
      if (sc === 'PageDown') { step(1); return true; }
      if (sc === 'Home' || sc === 'T') { go(new Date()); return true; }
      if (sc === 'ArrowLeft' && !searchQ) { step(-1); return true; }
      if (sc === 'ArrowRight' && !searchQ) { step(1); return true; }
      if (sc === 'F5') { void load(); return true; }
      if (sc === 'Ctrl+F' && hs) { hs.focus(); return true; }
      return false;
    },
  } as ReturnType<Parameters<typeof registerComponent>[1]> & { shortcut(sc: string): boolean };
});

registerComponent('kalendar-import', ({ desc, win, wm }) => {
  const p = desc.payload as { calendars: { value: string; label: string }[]; defaultCalendar: string | null; file: { id: string; name: string } | null };
  let file = p.file;
  const calSel = h('select', { class: 'input' }, p.calendars.map((c) => h('option', { value: c.value, selected: c.value === p.defaultCalendar }, c.label)));
  const fileName = h('span', { class: 'import-file' }, file ? file.name : '—');
  const pick = h('button', { type: 'button', class: 'btn' }, icon('upload'), t('kalendar.import.pick'));
  const run = h('button', { type: 'button', class: 'btn primary' }, t('kalendar.import.run'));
  const result = h('div', { class: 'import-result' });
  pick.addEventListener('click', async () => {
    const picked = await pickFile({ mode: 'upload', multiple: false, title: t('kalendar.import.pick'), parent: win });
    const item: StorageItem | undefined = picked?.[0];
    if (item) {
      file = { id: item.id, name: item.name };
      fileName.textContent = item.name;
    }
  });
  run.addEventListener('click', async () => {
    if (!file) {
      toast(t('kalendar.import.no_file'), 'warn');
      return;
    }
    run.disabled = true;
    run.textContent = t('kalendar.import.running');
    try {
      const r = await action<{ created: number; updated: number; skipped: number }>(desc.app, desc.window, 'run', desc.params as Record<string, unknown>, { fileId: file.id, calendar: calSel.value });
      result.replaceChildren(h('p', null, icon('check', 'icon cell-check'), ' ', t('kalendar.import.done', r)), h('button', { type: 'button', class: 'btn', onClick: () => void wm.open({ app: 'kalendar', window: 'kalendar' }) }, t('kalendar.import.open')));
      events.kick();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    } finally {
      run.disabled = false;
      run.textContent = t('kalendar.import.run');
    }
  });
  // nový vzhled: Importovat v liště záhlaví (stav tlačítka podle běhu)
  const sb = simpleBar(win, wm, () => [{ id: 'run', label: run.textContent ?? t('kalendar.import.run'), icon: 'import', place: 'primary', disabled: run.disabled, onClick: () => run.click() }]);
  if (sb.active) new MutationObserver(() => sb.refresh()).observe(run, { attributes: true, childList: true, characterData: true, subtree: true });
  return {
    setMode: () => sb.refresh(),
    el: h('div', { class: 'view kontakty-import' }, h('p', { class: 'muted small' }, t('kalendar.import.hint')), h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('kalendar.import.calendar')), calSel), h('div', { class: 'field' }, h('span', { class: 'field-label' }, t('kalendar.import.file')), h('div', { class: 'row' }, pick, fileName)), sb.active ? '' : h('div', { class: 'row' }, run), result),
  };
});
